import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
} from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Form, Row } from 'reactstrap';
import IdentificationSearchFilter from '../../../components/IdentificationSearchFilter';
import { IdentificationSearchFilterValue } from '../../../components/IdentificationSearchFilter/types';
import { TranslationsKeys } from '../../../locales/translations';
import { useRiskEvaluationSessionDataLoader } from '../loaders/StudentRiskEvaluationSessionDataLoader';
import { StudentWarningsRiskLevel } from '../types';

type StudentRiskEvaluationSessionFiltersParams = {
  name: string;
  identification?: Partial<IdentificationSearchFilterValue>;
  promo: SelectOptionType | null;
  group: SelectOptionType | null;
  riskLevel: SelectOptionType | null;
  definedRiskLevel: SelectOptionType | null;
};

const defaultValues: StudentRiskEvaluationSessionFiltersParams = {
  name: '',
  identification: undefined,
  promo: null,
  group: null,
  riskLevel: null,
  definedRiskLevel: null,
};

const StudentRiskEvaluationSessionFilters = () => {
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_RISK_EVALUATION_SESSIONS_STUDENTS,
  );
  const { loading, query, updateQuery } = useRiskEvaluationSessionDataLoader();
  const {
    handleSubmit,
    reset,
    control,
  } = useForm<StudentRiskEvaluationSessionFiltersParams>({
    defaultValues,
  });

  const riskLevelsOptions: SelectOptionType[] = useMemo(
    () => [
      {
        label: t('riskLevels.low'),
        value: StudentWarningsRiskLevel.Low,
      },
      {
        label: t('riskLevels.medium'),
        value: StudentWarningsRiskLevel.Medium,
      },
      {
        label: t('riskLevels.high'),
        value: StudentWarningsRiskLevel.High,
      },
    ],
    [t],
  );

  const handleSearch = useCallback(
    (filters: StudentRiskEvaluationSessionFiltersParams) => {
      let useIdentificationFilter = !!filters.identification?.identificationDocument?.trim();
      let identificationFilterIsValid = false;
      if (
        useIdentificationFilter &&
        filters.identification?.identificationDocument &&
        filters.identification?.identificationDocumentType &&
        filters.identification?.identificationDocumentCountry
      ) {
        identificationFilterIsValid = true;
      }
      console.log('useIdentificationFilter', useIdentificationFilter);
      console.log('identificationFilterIsValid', identificationFilterIsValid);
      if (!identificationFilterIsValid && useIdentificationFilter) {
        return;
      }
      updateQuery({
        ...query,
        page: 1,
        name: filters.name || undefined,
        riskLevel: filters.riskLevel?.value?.toString() || undefined,
        definedRiskLevel:
          filters.definedRiskLevel?.value?.toString() || undefined,
        group: filters.group?.value || undefined,
        promo: filters.promo?.value || undefined,
        identification:
          filters.identification && useIdentificationFilter
            ? filters.identification
            : undefined,
      });
    },
    [updateQuery, query],
  );

  const handleClear = useCallback(() => {
    reset(defaultValues);
    updateQuery({
      ...query,
      page: 1,
      name: undefined,
      riskLevel: undefined,
      definedRiskLevel: undefined,
      group: undefined,
      promo: undefined,
      identification: undefined,
    });
  }, [reset, query, updateQuery]);

  return (
    <Container className="mt-5" fluid>
      <Form onSubmit={handleSubmit(handleSearch)}>
        <Row className="pb-4 gutters-filters">
          <Col xs={12} lg={7}>
            <Controller
              control={control}
              name="identification"
              render={({ field }) => (
                <IdentificationSearchFilter
                  variant="outlined"
                  value={field.value}
                  maxWidth="100%"
                  onChange={field.onChange}
                />
              )}
            />
          </Col>
          <Col xs={12} lg={5}>
            <TextOutlinedInput
              name="name"
              label={t('filters.name.label')}
              placeholder={t('filters.name.placeholder')}
              control={control}
              disabled={loading}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <OutlinedSelect
              name="promo"
              options={[]}
              label={t('filters.promo.label')}
              placeholder={t('filters.promo.placeholder')}
              isClearable={true}
              disabled={true}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <OutlinedSelect
              name="group"
              options={[]}
              label={t('filters.group.label')}
              placeholder={t('filters.group.placeholder')}
              isClearable={true}
              disabled={true}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <OutlinedSelect
              name="riskLevel"
              options={riskLevelsOptions}
              label={t('filters.riskLevel.label')}
              placeholder={t('filters.riskLevel.placeholder')}
              control={control}
              isClearable={true}
              disabled={loading || riskLevelsOptions.length < 1}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <OutlinedSelect
              name="definedRiskLevel"
              options={riskLevelsOptions}
              label={t('filters.definedRiskLevel.label')}
              placeholder={t('filters.definedRiskLevel.placeholder')}
              control={control}
              isClearable={true}
              disabled={loading || riskLevelsOptions.length < 1}
            />
          </Col>
          <Col xs={12} md={12} className="mt-4">
            <div className="d-flex justify-content-end">
              <Button
                text={t('common:actions.search')}
                size="md"
                type="submit"
                disabled={loading}
              />
              <Button
                text={t('common:actions.clean')}
                className="ml-2"
                size="md"
                outlined
                type="button"
                disabled={loading}
                onClick={handleClear}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default StudentRiskEvaluationSessionFilters;
